/**
 * Beauty Warehouse — Menu drawer
 * Hardened: delegated listeners, both [open] and [data-open] supported,
 * idempotent so theme-editor re-renders don't double-bind.
 *
 * FIX: Moves #MenuDrawer to <body> on load so that no ancestor
 * transform / will-change / contain (applied by base-theme JS on
 * collection, product, 404 etc. pages) can create a containing block
 * that traps position:fixed children.
 */
(function () {
  if (typeof document === 'undefined') return;
  if (window.__bwMenuDrawerBound) return;
  window.__bwMenuDrawerBound = true;

  let lastFocus = null;

  const getDrawer = () => document.querySelector('[data-menu-drawer]');

  /* ── Relocate drawer to <body> ── */
  function hoist() {
    const drawer = getDrawer();
    if (!drawer) return;
    /* Already a direct child of body? Nothing to do. */
    if (drawer.parentNode === document.body) return;
    document.body.appendChild(drawer);
    /* Belt-and-braces: force inline resets so even inline styles
       set by base-theme JS on the element itself are overridden. */
    drawer.style.setProperty('transform', 'none', 'important');
    drawer.style.setProperty('will-change', 'auto', 'important');
    drawer.style.setProperty('contain', 'none', 'important');
  }

  /* Run as early as possible, and again after full load in case
     Shopify's base-theme JS re-renders the section group. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hoist);
  } else {
    hoist();
  }
  window.addEventListener('load', hoist);

  /* Also hoist after Shopify theme-editor events */
  document.addEventListener('shopify:section:load', hoist);
  document.addEventListener('shopify:section:reorder', hoist);

  /* ── MutationObserver safety net ──
     If anything ever re-parents the drawer or adds inline transforms,
     we catch it and fix it immediately. */
  const mo = new MutationObserver(function () {
    const drawer = getDrawer();
    if (!drawer) return;
    if (drawer.parentNode !== document.body) {
      document.body.appendChild(drawer);
    }
    /* Strip any inline transform the base theme may have injected */
    var inlineTransform = drawer.style.transform;
    if (inlineTransform && inlineTransform !== 'none') {
      drawer.style.setProperty('transform', 'none', 'important');
    }
  });
  /* Observe the whole document for structural changes */
  mo.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style']
  });

  /* ── Open / Close ── */
  function open() {
    const drawer = getDrawer();
    if (!drawer) return;
    /* Make sure we're still on body before opening */
    if (drawer.parentNode !== document.body) {
      document.body.appendChild(drawer);
    }
    lastFocus = document.activeElement;
    drawer.setAttribute('open', '');
    drawer.setAttribute('data-open', 'true');
    document.documentElement.style.overflow = 'hidden';
    document.querySelectorAll('[data-menu-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'true');
    });
    var firstLink = drawer.querySelector('.menu-drawer__link, button, a');
    try { firstLink && firstLink.focus({ preventScroll: true }); } catch (_) {}
  }

  function close() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.removeAttribute('open');
    drawer.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
    document.querySelectorAll('[data-menu-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
    try { lastFocus && lastFocus.focus && lastFocus.focus({ preventScroll: true }); } catch (_) {}
  }

  /* Use capture phase so we fire before any other handler can
     stopPropagation in the bubble phase. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-menu-toggle]')) { e.preventDefault(); open(); return; }
    if (e.target.closest('[data-menu-close]'))  { e.preventDefault(); close(); return; }
  }, true);

  document.addEventListener('keydown', function (e) {
    const drawer = getDrawer();
    if (e.key === 'Escape' && drawer && (drawer.hasAttribute('open') || drawer.hasAttribute('data-open'))) close();
  });

  /* ── Sticky header scroll state ── */
  window.addEventListener('scroll', function () {
    const header = document.querySelector('.header');
    if (!header) return;
    if (window.scrollY > 12) header.classList.add('header--scrolled');
    else header.classList.remove('header--scrolled');
  }, { passive: true });
})();
